import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  InputAdornment,
  Tooltip,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  Agriculture as AgricultureIcon,
  Phone as PhoneIcon,
  Badge as BadgeIcon,
  Close as CloseIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

interface Farmer {
  id: number;
  civilid: string;
  name: string;
  phone?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
  surveys_count?: number;
  crops_count?: number;
}

interface Survey {
  id: number;
  survey_number: string;
  date_collected: string;
  crop_name_ar: string;
  crop_name_en: string;
  village_name_ar: string;
  village_name_en: string;
  full_location_path: string;
  is_local_seed: boolean;
  has_photos: boolean;
}

const FarmersPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [farmers, setFarmers] = useState<Farmer[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openSurveysDialog, setOpenSurveysDialog] = useState(false);
  const [selectedFarmer, setSelectedFarmer] = useState<Farmer | null>(null);
  const [farmerSurveys, setFarmerSurveys] = useState<Survey[]>([]);
  const [loadingSurveys, setLoadingSurveys] = useState(false);
  const [formData, setFormData] = useState({
    civilid: '',
    name: '',
    phone: '',
    notes: '',
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'warning' | 'info',
  });
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 10,
    page: 0
  });

  useEffect(() => {
    fetchFarmers();
  }, []);

  const fetchFarmers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/seeds/farmers/');
      setFarmers(response.data.results || response.data);
    } catch (error) {
      console.error('Error fetching farmers:', error);
      setSnackbar({
        open: true,
        message: t('errorFetchingFarmers'),
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchFarmerSurveys = async (farmerId: number) => {
    try {
      setLoadingSurveys(true);
      const response = await api.get(`/seeds/farmers/${farmerId}/surveys/`);
      setFarmerSurveys(response.data.results || response.data);
    } catch (error) {
      console.error('Error fetching farmer surveys:', error);
      setSnackbar({
        open: true,
        message: t('errorFetchingSurveys'),
        severity: 'error',
      });
    } finally {
      setLoadingSurveys(false);
    }
  };

  const handleOpenDialog = (farmer?: Farmer) => {
    if (farmer) {
      setSelectedFarmer(farmer);
      setFormData({
        civilid: farmer.civilid,
        name: farmer.name,
        phone: farmer.phone || '',
        notes: farmer.notes || '',
      });
    } else {
      setSelectedFarmer(null);
      setFormData({
        civilid: '',
        name: '',
        phone: '',
        notes: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedFarmer(null);
    setFormData({
      civilid: '',
      name: '',
      phone: '',
      notes: '',
    });
  };

  const handleSave = async () => {
    try {
      if (!formData.civilid || !formData.name) {
        setSnackbar({
          open: true,
          message: t('requiredFieldsMissing'),
          severity: 'warning',
        });
        return;
      }

      if (selectedFarmer) {
        await api.patch(`/seeds/farmers/${selectedFarmer.id}/`, formData);
        setSnackbar({
          open: true,
          message: t('farmerUpdatedSuccessfully'),
          severity: 'success',
        });
      } else {
        await api.post('/seeds/farmers/', formData);
        setSnackbar({
          open: true,
          message: t('farmerAddedSuccessfully'),
          severity: 'success',
        });
      }
      handleCloseDialog();
      fetchFarmers();
    } catch (error: any) {
      console.error('Error saving farmer:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.civilid?.[0] || t('errorSavingFarmer'),
        severity: 'error',
      });
    }
  };

  const handleDelete = async () => {
    if (!selectedFarmer) return;
    
    try {
      await api.delete(`/seeds/farmers/${selectedFarmer.id}/`);
      setSnackbar({
        open: true,
        message: t('farmerDeletedSuccessfully'),
        severity: 'success',
      });
      setOpenDeleteDialog(false);
      setSelectedFarmer(null);
      fetchFarmers();
    } catch (error) {
      console.error('Error deleting farmer:', error);
      setSnackbar({
        open: true,
        message: t('errorDeletingFarmer'),
        severity: 'error',
      });
    }
  };

  const handleViewSurveys = (farmer: Farmer) => {
    setSelectedFarmer(farmer);
    setOpenSurveysDialog(true);
    fetchFarmerSurveys(farmer.id);
  };

  const filteredFarmers = farmers.filter(farmer =>
    farmer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    farmer.civilid.includes(searchTerm) ||
    (farmer.phone && farmer.phone.includes(searchTerm))
  );

  const columns: GridColDef[] = [
    {
      field: 'civilid',
      headerName: t('civilId'),
      width: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BadgeIcon fontSize="small" color="primary" />
          <Typography variant="body2">{params.value}</Typography>
        </Box>
      ),
    },
    {
      field: 'name',
      headerName: t('name'),
      flex: 1,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AgricultureIcon fontSize="small" color="success" />
          <Typography variant="body2" fontWeight="medium">
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'phone',
      headerName: t('phone'),
      width: 150,
      renderCell: (params: GridRenderCellParams) => (
        params.value ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PhoneIcon fontSize="small" color="action" />
            <Typography variant="body2">{params.value}</Typography>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t('noPhone')}
          </Typography>
        )
      ),
    },
    {
      field: 'surveys_count',
      headerName: t('surveysCount'),
      width: 130,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value || 0}
          color={params.value > 0 ? 'primary' : 'default'}
          size="small"
          onClick={() => params.value > 0 && handleViewSurveys(params.row)}
          sx={{ cursor: params.value > 0 ? 'pointer' : 'default' }}
        />
      ),
    },
    {
      field: 'crops_count',
      headerName: t('cropsCount'),
      width: 130,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value || 0}
          color={params.value > 0 ? 'success' : 'default'}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: 'created_at',
      headerName: t('createdAt'),
      width: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" color="text.secondary">
          {new Date(params.value).toLocaleDateString(
            i18n.language === 'ar' ? 'ar-SA' : 'en-US'
          )}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: t('actions'),
      width: 150,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box>
          <Tooltip title={t('viewSurveys')}>
            <IconButton
              size="small"
              color="info"
              onClick={() => handleViewSurveys(params.row)}
            >
              <ViewIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('editFarmer')}>
            <IconButton
              size="small"
              color="primary"
              onClick={() => handleOpenDialog(params.row)}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('deleteFarmer')}>
            <IconButton
              size="small"
              color="error"
              onClick={() => {
                setSelectedFarmer(params.row);
                setOpenDeleteDialog(true);
              }}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom fontWeight="bold">
          {t('farmersManagement')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t('farmersPageDescription')}
        </Typography>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <AgricultureIcon fontSize="large" color="primary" />
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {farmers.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('totalFarmers')}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <ViewIcon fontSize="large" color="success" />
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {farmers.reduce((sum, f) => sum + (f.surveys_count || 0), 0)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('totalSurveys')}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <AgricultureIcon fontSize="large" color="warning" />
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {farmers.reduce((sum, f) => sum + (f.crops_count || 0), 0)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('totalCrops')}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Actions Bar */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            placeholder={t('searchFarmers')}
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ flexGrow: 1, maxWidth: 400 }}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{
              background: 'linear-gradient(45deg, #4CAF50 30%, #81C784 90%)',
              boxShadow: '0 3px 5px 2px rgba(76, 175, 80, .3)',
            }}
          >
            {t('addFarmer')}
          </Button>
        </Box>
      </Paper>

      {/* Data Grid */}
      <Paper sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={filteredFarmers}
          columns={columns}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[5, 10, 25, 50]}
          loading={loading}
          disableRowSelectionOnClick
          sx={{
            '& .MuiDataGrid-root': {
              border: 'none',
            },
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid rgba(224, 224, 224, 1)',
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: 'background.paper',
              borderBottom: '2px solid',
              borderColor: 'primary.main',
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        />
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedFarmer ? t('editFarmer') : t('addFarmer')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label={t('civilId')}
              value={formData.civilid}
              onChange={(e) => setFormData({ ...formData, civilid: e.target.value })}
              fullWidth
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <BadgeIcon />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label={t('name')}
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label={t('phone')}
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIcon />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label={t('notes')}
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              fullWidth
              multiline
              rows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('cancel')}</Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            {selectedFarmer ? t('update') : t('add')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>{t('deleteFarmer')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('confirmDeleteFarmer')} "{selectedFarmer?.name}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>{t('cancel')}</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            {t('delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Surveys Dialog */}
      <Dialog
        open={openSurveysDialog}
        onClose={() => setOpenSurveysDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">
              {t('farmerSurveys')}: {selectedFarmer?.name}
            </Typography>
            <IconButton onClick={() => setOpenSurveysDialog(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {loadingSurveys ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : farmerSurveys.length > 0 ? (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('surveyNumber')}</TableCell>
                    <TableCell>{t('dateCollected')}</TableCell>
                    <TableCell>{t('crop')}</TableCell>
                    <TableCell>{t('village')}</TableCell>
                    <TableCell align="center">{t('actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {farmerSurveys.map((survey) => (
                    <TableRow key={survey.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {survey.survey_number}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {new Date(survey.date_collected).toLocaleDateString(
                          i18n.language === 'ar' ? 'ar-SA' : 'en-US'
                        )}
                      </TableCell>
                      <TableCell>
                        {i18n.language === 'ar' ? survey.crop_name_ar : survey.crop_name_en}
                      </TableCell>
                      <TableCell>
                        {i18n.language === 'ar' ? survey.village_name_ar : survey.village_name_en}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title={t('viewDetails')}>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => {
                              setOpenSurveysDialog(false);
                              navigate(`/survey/details/${survey.id}`);
                            }}
                          >
                            <ArrowForwardIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography variant="body1" sx={{ p: 2, textAlign: 'center' }}>
              {t('noSurveysFound')}
            </Typography>
          )}
        </DialogContent>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FarmersPage;
